import { type z } from 'zod';
import { string as __eliadmin_zod_0, object as __eliadmin_zod_1, enum as __eliadmin_zod_2, boolean as __eliadmin_zod_3, email as __eliadmin_zod_4 } from 'zod';
import { ensureZodDefaults as __eliadmin_zod_defaults } from '@eliadmin/core/src/build/zod-defaults';
__eliadmin_zod_defaults();

import { meetsRequiredPasswordRules } from '@eliadmin/core/src/components/dlz/forms/password-rules';
import { REGISTER_COPY } from '@eliadmin/core/src/components/dlz/auth/register/register-copy';
import { isValidPhone } from '@eliadmin/core/src/components/dlz/auth/register/register-values';
import type { FormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';

export type Persona = 'individual' | 'company';

/**
 * One flat form for both personas, so switching the „Registrujem sa ako“ control keeps what was
 * typed: name / surname / phone are the person (individual) or the contact person (company), the
 * address fields are the billing address (individual) or the company seat. The rules that depend on
 * the persona or on the „iná adresa“ checkbox are in `superRefine`.
 *
 * Deliberately no format rules for IČO / DIČ / IČ DPH / PSČ: neither the core schemas nor the
 * checkout validate them and the backend also accepts foreign companies.
 */
export const registerFormSchema = (VM: FormValidationMessages) => {
  const text = __eliadmin_zod_0().trim();

  return __eliadmin_zod_1({
      persona: __eliadmin_zod_2(['individual', 'company']),
      email: text
        .min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE())
        .email(VM.EMAIL_INVALID_VALIDATION_MESSAGE()),
      password: __eliadmin_zod_0(),
      password_confirmation: __eliadmin_zod_0(),
      name: text,
      surname: text,
      phone: text,
      contact_email: text,
      address: text,
      city: text,
      postcode: text,
      country: text,
      company: text,
      ico: text,
      dic: text,
      ic_dph: text,
      different_delivery_address: __eliadmin_zod_3(),
      delivery_company: text,
      delivery_contact: text,
      delivery_address: text,
      delivery_city: text,
      delivery_postcode: text,
      delivery_country: text,
      consent: __eliadmin_zod_3(),
      business_account: __eliadmin_zod_3(),
      newsletter: __eliadmin_zod_3(),
      newsletter_return_url: __eliadmin_zod_0(),
    })
    .superRefine((values, ctx) => {
      const issue = (path: keyof typeof values, message: string) =>
        ctx.addIssue({ code: 'custom', path: [path], message });
      const required = (...paths: (keyof typeof values)[]) => {
        for (const path of paths) {
          if (!values[path]) issue(path, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE());
        }
      };

      required('name', 'surname');

      if (!values.phone) issue('phone', VM.CANT_BE_EMPTY_PHONE_VALIDATION_MESSAGE());
      else if (!isValidPhone(values.phone)) issue('phone', REGISTER_COPY.personal.phoneInvalid);

      if (!values.password) issue('password', VM.CANT_BE_EMPTY_VALIDATION_MESSAGE());
      else if (!meetsRequiredPasswordRules(values.password)) {
        issue('password', REGISTER_COPY.credentials.passwordRules);
      }

      if (!values.password_confirmation) {
        issue('password_confirmation', VM.CANT_BE_EMPTY_VALIDATION_MESSAGE());
      } else if (values.password_confirmation !== values.password) {
        issue('password_confirmation', VM.PASSWORD_CONFIRMATION_DONT_MATCH_VALIDATION_MESSAGE());
      }

      if (values.persona === 'company') {
        required('company', 'ico', 'dic');
        if (!values.contact_email) issue('contact_email', VM.CANT_BE_EMPTY_VALIDATION_MESSAGE());
        else if (!__eliadmin_zod_4().safeParse(values.contact_email).success) {
          issue('contact_email', VM.EMAIL_INVALID_VALIDATION_MESSAGE());
        }
      }

      if (values.different_delivery_address) {
        required('delivery_address', 'delivery_city', 'delivery_postcode', 'delivery_country');
        if (values.persona === 'company') required('delivery_company');
      }

      if (!values.consent) {
        issue('consent', `${VM.TOS_VALIDATION_MESSAGE()}. ${VM.GDPR_VALIDATION_MESSAGE()}`);
      }
    });
};

export type RegisterFormSchema = ReturnType<typeof registerFormSchema>;
export type RegisterFormInput = z.input<RegisterFormSchema>;
export type RegisterFormValues = z.output<RegisterFormSchema>;
